import { useCallback, useEffect, useState } from 'react'
import { Grid, Box, SxProps } from '@mui/material'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import { InputField } from 'src/components/shared/ui/form/fields'
import AutoCompleteField from 'src/components/shared/ui/form/fields/AutoCompleteField'
import useNumberValidations from 'src/components/shared/ui/form/fields/hooks/useNumberValidations'
import NumeroCompuestoField from 'src/components/views/EscritosLexnet/components/shared/components/fields/NumeroCompuesto/NumeroCompuestoField'
import ProcedimientoFormView from 'src/components/views/EscritosLexnet/components/shared/components/forms/ProcedimientoForm/ProcedimientoFormView'
import { ProcedimientoFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/procedimientoTypes'
import { FormField } from 'src/enums/Form'
import { useGetProcedimientoNotPiezaTipo } from 'src/hooks/fetch/useCatalogoGeneralFilters'
import { Orden, OrganoJudicial } from 'src/interfaces/Catalog'
import { theme } from 'src/providers/ThemeProvider'

type Props = {
  organoJudicial: OrganoJudicial
  orden: Orden
  checkIfDisabledField?: () => boolean
  onClearProcedimientoFieldset?: () => void
}

const styles: SxProps = {
  fontFamily: theme.corporate.secondaryFont,
  fontSize: '12px',
  color: theme.colors.blue04,
  textTransform: 'uppercase',
  marginBottom: '14px'
}

function ProcedimientoForm({
  organoJudicial,
  orden,
  checkIfDisabledField = () => false
}: Props): JSX.Element {
  const { t } = useTranslation()
  const { allowInteger } = useNumberValidations()

  const { values } = useFormikContext<ProcedimientoFormValues>()

  const [labels, setLabels] = useState({
    tipo: 'form.field.procedimiento_tipo',
    numero: 'form.field.procedimiento_numero'
  })

  const tipo = useGetProcedimientoNotPiezaTipo(
    FormField.PROCEDIMIENTO_TIPO_FORM,
    [values[FormField.PROCEDIMIENTO_TIPOLOGIA_FORM], organoJudicial, orden],
    organoJudicial?.adaptadoTest
  )

  const isPiezaSeparada = values[FormField.PROCEDIMIENTO_TIPOLOGIA_FORM]?.codigo === 'PS'

  const renderTipoLabel = useCallback(() => {
    if (isPiezaSeparada) {
      setLabels({
        tipo: 'form.field.procedimiento_tipo_pieza',
        numero: 'form.field.procedimiento_numero_pieza'
      })
    } else {
      setLabels({
        tipo: 'form.field.procedimiento_tipo',
        numero: 'form.field.procedimiento_numero'
      })
    }
  }, [isPiezaSeparada])

  function getProcedimientoLabel(option) {
    return option && option.texto
      ? `${option.texto} [${option.codigo}] (${orden.descripcion})`
      : option
  }

  function renderProcedimientoOption(props, option) {
    return (
      <li
        {...props}
        key={props.key}
      >{`${option.texto} [${option.codigo}] (${orden.descripcion})`}</li>
    )
  }

  function showPiezaSection(): JSX.Element | null {
    if (isPiezaSeparada) {
      return (
        <>
          <Grid item xs={6}>
            <InputField
              id={FormField.PROCEDIMIENTO_NUMERO_PIEZA_FORM}
              label={t('form.field.procedimiento_pieza')}
              onChange={allowInteger}
              maxLength={4}
            />
          </Grid>
          <Grid item xs={6} md={6} />
          <Grid item xs={12}>
            <Box component="span" sx={styles}>
              {t('form.field.origen_pieza')}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <AutoCompleteField
              id={FormField.PROCEDIMIENTO_TIPO_ORIGEN_FORM}
              items={tipo}
              label={t('form.field.procedimiento_tipo_origen')}
              itemName="texto"
              getOptionLabelCallback={getProcedimientoLabel}
              renderOptionCallback={renderProcedimientoOption}
              sortBy
              required
            />
          </Grid>
          <Grid item xs={6}>
            <NumeroCompuestoField
              label={t('form.field.procedimiento_numero_pieza_origen')}
              ids={{
                number: FormField.PROCEDIMIENTO_NUMERO_ORIGEN_FORM,
                year: FormField.PROCEDIMIENTO_YEAR_ORIGEN_FORM
              }}
            />
          </Grid>
        </>
      )
    }
    return null
  }

  useEffect(() => {
    renderTipoLabel()
  }, [isPiezaSeparada, renderTipoLabel])

  return (
    <ProcedimientoFormView
      values={values}
      labels={labels}
      showPiezaSection={showPiezaSection}
      organoJudicial={organoJudicial}
      orden={orden}
      checkIfDisabledField={checkIfDisabledField}
    />
  )
}

export default ProcedimientoForm
